import React, { useState, useEffect, ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  // useMediaQuery,
  Skeleton,
  InputBase,
  Paper,
  Button
} from '@mui/material';
import { productsApi, categoriesApi, Product, Category } from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { styled } from '@mui/material/styles';
import { cartService } from '../services/cart';

const PaginationContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const PaginationButton = styled('button')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover:not(:disabled)': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
        transform: 'translateY(-2px)',
    },
    '&:disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        cursor: 'not-allowed',
        borderColor: theme.palette.divider,
    },
    [theme.breakpoints.down('sm')]: {
        width: 36,
        height: 36,
    },
}));

const PaginationInfo = styled('span')(({ theme }) => ({
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    minWidth: 100,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.8rem',
        minWidth: 80,
    },
}));

const Pagination: React.FC<{
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
    return (
        <PaginationContainer>
            <PaginationButton 
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                <FaChevronLeft />
            </PaginationButton>
            <PaginationInfo>
                Page {currentPage} of {totalPages}
            </PaginationInfo>
            <PaginationButton 
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                <FaChevronRight />
            </PaginationButton>
        </PaginationContainer>
    );
};

const ProductList = (): ReactElement => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem(`productList_page_${categoryId}`);
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [itemsPerPage] = useState(12);
  const [cartMessage, setCartMessage] = useState<{ [productId: number]: string }>({});

  const getImageUrl = (imageUrl: string | undefined | null) => {
    const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
    
    try {
        if (!imageUrl) return defaultPlaceholder;
        
        // If it's an S3 URL, return it directly without any modification
        if (imageUrl.includes('s3.amazonaws.com')) {
            return imageUrl;
        }
        
        // If it's already a full URL (non-S3), return it directly
        if (imageUrl.startsWith('http')) {
            return imageUrl;
        }

        // For backward compatibility with old local storage URLs
        const cleanUrl = imageUrl
            .replace(/['"]/g, '')
            .replace(/;/g, '')
            .replace(/^\/+/, '')
            .replace(/\/+/g, '/')
            .trim();

        let filename = cleanUrl;
        
        const patterns = [
            /^uploads\/products\/(.+)$/,
            /^products\/(.+)$/,
            /^app\/uploads\/products\/(.+)$/,
            /^(.+)$/
        ];

        for (const pattern of patterns) {
            const match = cleanUrl.match(pattern);
            if (match && match[1]) {
                filename = match[1];
                break;
            }
        }

        filename = filename
            .replace(/[^\w\-.]/g, '')
            .replace(/\.+/g, '.')
            .toLowerCase();

        if (!filename || filename.length < 5) return defaultPlaceholder;

        // For old local storage URLs, use the API base URL
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://skylightbackend-production.up.railway.app';
        const finalUrl = `${apiBaseUrl}/uploads/products/${filename}`;

        try {
            new URL(finalUrl);
            return finalUrl;
        } catch {
            return defaultPlaceholder;
        }
    } catch (error) {
        console.error('[ProductList] Error formatting URL:', error);
        return defaultPlaceholder;
    }
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
    if (!img.src.includes('placehold.co')) {
      img.src = defaultPlaceholder;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsResponse, categoryResponse] = await Promise.all([
          productsApi.getByCategoryId(Number(categoryId)),
          categoriesApi.getById(Number(categoryId))
        ]);

        const processedProducts = productsResponse?.data?.data?.products || [];
        const categoryData = categoryResponse?.data?.data?.category;

        setProducts(processedProducts);
        setFilteredProducts(processedProducts);

        if (categoryData) {
          setCategory(categoryData);
        }

        setError(null);
      } catch (err) {
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchData();
    }
  }, [categoryId]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProducts(filtered);
      setCurrentPage(1);
    } else {
      setFilteredProducts(products);
    }
  }, [searchQuery, products]);

  const handleProductClick = (productId: number) => {
    const scrollPosition = window.pageYOffset;
    localStorage.setItem(`productList_scroll_${categoryId}`, scrollPosition.toString());
    localStorage.setItem(`productList_page_${categoryId}`, currentPage.toString());
    navigate(`/product/${productId}`);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  useEffect(() => {
    const savedScrollPosition = localStorage.getItem(`productList_scroll_${categoryId}`);
    if (savedScrollPosition) {
      window.scrollTo({
        top: parseInt(savedScrollPosition, 10),
        behavior: 'auto'
      });
    }
  }, [categoryId]);

  useEffect(() => {
    localStorage.setItem(`productList_page_${categoryId}`, currentPage.toString());
  }, [currentPage, categoryId]);

  useEffect(() => {
    const cleanup = () => {
      if (!categoryId) return;
      const currentPath = window.location.pathname;
      if (!currentPath.includes(`/category/${categoryId}`) && !currentPath.includes('/product/')) {
        localStorage.removeItem(`productList_scroll_${categoryId}`);
        localStorage.removeItem(`productList_page_${categoryId}`);
      }
    };

    window.addEventListener('popstate', cleanup);
    return () => {
      window.removeEventListener('popstate', cleanup);
      cleanup();
    };
  }, [categoryId]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    if (!localStorage.getItem(`productList_scroll_${categoryId}`)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Grid container spacing={3}>
          {[...Array(12)].map((_, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <AppBar 
        position="sticky" 
        color="inherit" 
        elevation={0}
        sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            onClick={() => navigate('/')}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {category?.name} ({filteredProducts.length} Products)
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            mb: 1
          }}
        >
          <SearchIcon sx={{ p: 1, color: 'text.secondary' }} />
          <InputBase
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              ml: 1,
              flex: 1,
              '& input': {
                py: 1,
                fontSize: '0.875rem'
              }
            }}
          />
        </Paper>
      </Box>

      <Grid container spacing={2}>
        {loading && (
          Array.from(new Array(8)).map((_, index) => (
            <Grid item key={index} xs={6} sm={4} md={3} lg={2.4}>
              <Card>
                <Skeleton variant="rectangular" height={140} />
                <CardContent>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
        
        {error && !loading && (
          <Grid item xs={12}>
            <Typography color="error">{error}</Typography>
          </Grid>
        )}
        
        {!loading && !error && currentItems.map((product) => (
          <Grid item key={product.id} xs={6} sm={4} md={3} lg={2.4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => handleProductClick(product.id)}
            >
              <Box 
                sx={{ 
                  position: 'relative',
                  pt: '90%',
                  bgcolor: '#f5f5f5',
                  overflow: 'hidden'
                }}
              >
                <CardMedia
                  component="img"
                  image={product.imageUrl && product.imageUrl.includes('s3.amazonaws.com') ? product.imageUrl : getImageUrl(product.imageUrl)}
                  alt={product.name}
                  onError={handleImageError}
                  sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    p: { xs: 0.5, sm: 1 },
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              </Box>

              <CardContent 
                sx={{ 
                  p: { xs: 1, sm: 1.5 }, 
                  '&:last-child': { pb: { xs: 1, sm: 1.5 } },
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                <Typography 
                  variant="subtitle2"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: 0.5,
                    lineHeight: 1.2,
                    height: '2.4em',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {product.name}
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  gap: 0.5,
                  mt: 'auto' 
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    MRP: ₹{(() => {
                      const price = parseFloat(String(product.price));
                      return !isNaN(price) ? price.toFixed(2) : '0.00';
                    })()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: product.stock > 0 ? 'success.main' : 'error.main',
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      ml: { xs: 0, sm: 'auto' }
                    }}
                  >
                    {product.stock > 0 ? `In Stock (${product.stock})` : 'Out of Stock'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{ mt: 1, minWidth: 120 }}
                  disabled={product.stock <= 0}
                  onClick={e => {
                    e.stopPropagation();
                    cartService.addToCart(product, 1);
                    setCartMessage(msg => ({ ...msg, [product.id]: 'Added!' }));
                    setTimeout(() => setCartMessage(msg => ({ ...msg, [product.id]: '' })), 1200);
                  }}
                >
                  Add to Cart
                </Button>
                {cartMessage[product.id] && (
                  <Typography color="success.main" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                    {cartMessage[product.id]}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </Container>
  );
};

export default ProductList; 